import React, { useState, useEffect } from 'react';
import { Button, Card, Col, Container, Form, Row, Spinner, Alert } from 'react-bootstrap';
import { BiCheckSquare } from 'react-icons/bi';
import { TiWarningOutline } from 'react-icons/ti';
import { CgPassword } from 'react-icons/cg';
import { FaRegThumbsUp } from 'react-icons/fa';
import passwordValidator from 'password-validator';
import { changePassword } from '.';

/**
 * Full-screen mandatory password change shown after login when the server
 * reports `passwordChangeRequired` (admin-created users with a temporary
 * password). There is no skip/close — the user must set a valid new password
 * before the app loads. On success, `onComplete` is called so the parent can
 * clear the flag and render the app.
 */
const ForcePasswordChange = (props) => {
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [schema, setSchema] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [hasMin, setHasMin] = useState(false);
  const [hasUpper, setHasUpper] = useState(false);
  const [hasLower, setHasLower] = useState(false);
  const [hasDigits, setHasDigits] = useState(false);
  const [hasSymbols, setHasSymbols] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [confirmMatch, setConfirmMatch] = useState(false);

  useEffect(() => {
    const s = new passwordValidator();
    s.is().min(8).is().max(100).has().uppercase().has().lowercase().has().digits().has().symbols();
    setSchema(s);
  }, []);

  useEffect(() => {
    if (!schema) return;
    const failed = schema.validate(newPass, { list: true }).reduce((map, item) => {
      map[item] = true;
      return map;
    }, {});
    setHasMin(!failed['min']);
    setHasUpper(!failed['uppercase']);
    setHasLower(!failed['lowercase']);
    setHasDigits(!failed['digits']);
    setHasSymbols(!failed['symbols']);
  }, [newPass, schema]);

  useEffect(() => {
    setPasswordValid(hasMin && hasUpper && hasLower && hasDigits && hasSymbols);
  }, [hasMin, hasUpper, hasLower, hasDigits, hasSymbols]);

  useEffect(() => {
    setConfirmMatch(confirmPass !== '' && confirmPass === newPass);
  }, [confirmPass, newPass]);

  const canSubmit = oldPass !== '' && passwordValid && confirmMatch && !submitting;

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      await changePassword(oldPass, newPass);
      props.onComplete();
    } catch (e) {
      // ApiError carries the server's structured error envelope on `.body`.
      setError(e?.body?.error || e?.message || 'Failed to change password.');
      setSubmitting(false);
    }
  };

  const Req = ({ ok, label }) => (
    <div className={ok ? 'valid-pass-req' : 'invalid-pass-req'}>
      {ok ? <BiCheckSquare /> : <TiWarningOutline />} {label}
    </div>
  );

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <Card style={{ maxWidth: '640px', width: '100%' }} className="shadow">
        <Card.Body className="p-4">
          <h4 className="pb-2">
            <CgPassword className="change-pass-header-icon mr-2" />
            Set a new password
          </h4>
          <p className="text-muted">
            Your account was created with a temporary password. You must choose a new
            password before continuing.
          </p>

          {error && (
            <Alert variant="danger" className="text-center py-2">
              {error}
            </Alert>
          )}

          <Row>
            <Col lg="5">
              <div className="pass-req">
                <div className="pass-req-header">Passwords must have:</div>
                <Req ok={hasMin} label="At least 8 characters" />
                <Req ok={hasUpper} label="1 uppercase letter" />
                <Req ok={hasLower} label="1 lowercase letter" />
                <Req ok={hasDigits} label="1 number" />
                <Req ok={hasSymbols} label="1 special character" />
              </div>
            </Col>
            <Col>
              <Form onSubmit={(e) => e.preventDefault()}>
                <Form.Group>
                  <Form.Label>Temporary Password</Form.Label>
                  <Form.Control
                    data-testid="force-pw-old"
                    type="password"
                    value={oldPass}
                    onChange={(e) => setOldPass(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="position-relative">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    data-testid="force-pw-new"
                    className={passwordValid ? 'validated-input' : undefined}
                    type="password"
                    value={newPass}
                    onChange={(e) => setNewPass(e.target.value)}
                  />
                  {passwordValid && <FaRegThumbsUp className="validated-input-icon" />}
                </Form.Group>
                <Form.Group className="position-relative">
                  <Form.Label>Confirm</Form.Label>
                  <Form.Control
                    data-testid="force-pw-confirm"
                    className={confirmMatch ? 'validated-input' : undefined}
                    type="password"
                    value={confirmPass}
                    onChange={(e) => setConfirmPass(e.target.value)}
                  />
                  {confirmMatch && <FaRegThumbsUp className="validated-input-icon" />}
                  <Form.Text>
                    {confirmPass !== '' && !confirmMatch && (
                      <small className="text-danger">Passwords do not match.</small>
                    )}
                  </Form.Text>
                </Form.Group>
                <div className="d-flex justify-content-end">
                  <Button variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
                    {submitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="mr-2" />
                        Saving…
                      </>
                    ) : (
                      'Set Password'
                    )}
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ForcePasswordChange;
